import { Aggregates, Category, Operation, Settings } from '../api/types';

/**
 * Средний месячный расход по реальным данным — для расчёта подушки
 * в «месяцах защиты». Берутся только ПОЛНЫЕ месяцы после stats_since;
 * если полных месяцев нет — run-rate текущего месяца.
 *
 * `aggregates` (план 3a) — серверные суммы по месяцам за всю историю. Если поле
 * есть, полные месяцы считаются по нему, и клиенту не нужна вся таблица
 * операций. Фоллбэк на расчёт по `operations` обязателен: снапшот из IndexedDB
 * переживает деплой и этого поля не имеет.
 *
 * ВНИМАНИЕ: результат уходит в БД — `FoundationCard.quickSet` (Goals.tsx) пишет
 * его в `goals.need`. Оба режима обязаны давать одинаковое число на одних и тех
 * же данных; это зафиксировано тестами в `__tests__/foundation.test.ts`.
 */
export function avgMonthlyExpense(
  operations: Operation[],
  categories: Category[],
  settings: Settings | null,
  now: Date = new Date(),
  aggregates?: Aggregates | null,
): number {
  const catType = new Map(categories.map((c) => [c.name, c.type] as const));
  const since = settings?.stats_since ?? null;
  const currentKey = monthKey(now);

  const byMonth = new Map<string, number>();
  for (const op of operations) {
    const t = catType.get(op.category) ?? op.type;
    if (t !== 'Расход') continue;
    if (since && op.date < since) continue;
    const key = op.date.slice(0, 7);
    byMonth.set(key, (byMonth.get(key) || 0) + op.amount);
  }

  // только полные месяцы (не текущий); месяц stats_since считаем полным, если дата — 1-е число
  const sinceFullFrom =
    since && !since.endsWith('-01') ? nextMonthKey(since.slice(0, 7)) : since?.slice(0, 7) ?? null;

  // Источник полных месяцев: серверный агрегат, если он есть, иначе — операции.
  // Агрегат не знает про stats_since и считает его месяц целиком, но такой месяц
  // всегда отсекается sinceFullFrom (кроме случая «1-е число», где резать нечего),
  // — расхождения с расчётом по операциям нет.
  const monthly: Iterable<readonly [string, number]> = aggregates?.monthly_totals
    ? aggregates.monthly_totals.map((m) => [m.month, m.expense] as const)
    : byMonth;

  let sum = 0;
  let n = 0;
  for (const [key, v] of monthly) {
    if (key >= currentKey) continue;
    if (sinceFullFrom && key < sinceFullFrom) continue;
    // Месяц без расходов в среднем не участвует: расчёт по операциям такой месяц
    // просто не видит, а агрегат отдаёт строку с expense = 0 (например, месяц
    // с одним доходом). Пропуск нуля уравнивает режимы.
    if (v === 0) continue;
    sum += v;
    n++;
  }
  if (n > 0) return sum / n;

  // fallback: run-rate текущего месяца — ВСЕГДА по операциям. Текущий месяц есть
  // в окне при любой его ширине, а агрегат не различает траты до и после
  // stats_since внутри месяца.
  const spent = byMonth.get(currentKey) || 0;
  const day = now.getDate();
  const daysTotal = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
  return day > 0 ? (spent / day) * daysTotal : 0;
}

function monthKey(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function nextMonthKey(key: string): string {
  const [y, m] = key.split('-').map(Number);
  const d = new Date(y, m, 1); // m уже следующий (0-based сдвиг)
  return monthKey(d);
}
